<template>
	<div class="component-section" @click="handleClick">
		<h3 class="section-title">Overlays & Panels</h3>
		<div class="overlays-content">
			<div class="overlays-grid">
				<div class="overlay-item">
					<label class="field-label">Popover Example</label>
					<div class="popover-preview">
						<div class="popover-static">
							<div class="popover-content">
								<h6>Popover Content</h6>
								<p>This is popover content with some text and actions.</p>
								<button class="popover-action-btn">Action</button>
							</div>
						</div>
					</div>
				</div>

				<div class="overlay-item">
					<label class="field-label">Tooltip Example</label>
					<div class="tooltip-preview">
						<div class="tooltip-static">
							This is a tooltip example
						</div>
					</div>
				</div>

				<div class="overlay-item">
					<label class="field-label">ConfirmDialog Example</label>
					<div class="confirm-dialog-preview">
						<div class="confirm-overlay">
							<div class="confirm-container">
								<div class="confirm-header">
									<i class="pi pi-exclamation-triangle confirm-icon"></i>
									<span class="confirm-title">Confirmation</span>
								</div>
								<div class="confirm-content">
									<p>Are you sure you want to proceed?</p>
								</div>
								<div class="confirm-footer">
									<button class="confirm-btn confirm-btn-secondary">Cancel</button>
									<button class="confirm-btn confirm-btn-primary">Confirm</button>
								</div>
							</div>
						</div>
					</div>
				</div>

				<div class="overlay-item">
					<label class="field-label">Sidebar Example</label>
					<div class="sidebar-preview">
						<div class="sidebar-container">
							<div class="sidebar-header">
								<span class="sidebar-title">Sidebar</span>
								<button class="sidebar-close-btn">
									<i class="pi pi-times"></i>
								</button>
							</div>
							<div class="sidebar-content">
								<p>This is sidebar content. You can place any content here.</p>
								<div class="sidebar-actions">
									<button class="sidebar-btn sidebar-btn-primary">Save</button>
									<button class="sidebar-btn sidebar-btn-secondary">Cancel</button>
								</div>
							</div>
						</div>
					</div>
				</div>

			</div>
		</div>

	</div>
</template>

<script setup lang="ts">
const emit = defineEmits<{
	(e: "elementClick", event: MouseEvent): void
}>()

const handleClick = (event: MouseEvent) => {
	emit("elementClick", event)
}
</script>

<style lang="scss" scoped>
.component-section {
	background: white;
	border-radius: 8px;
	padding: 1.5rem;
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
	margin-bottom: 1rem;
}

.section-title {
	font-size: 1.2rem;
	font-weight: 600;
	margin-bottom: 1rem;
	color: #374151;
	border-bottom: 2px solid #e5e7eb;
	padding-bottom: 0.5rem;
}

.overlays-content {
	display: flex;
	flex-direction: column;
	gap: 1.5rem;
}

.overlays-grid {
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 1rem;
}

.overlay-item,
.file-section {
	display: flex;
	flex-direction: column;
	gap: 0.75rem;
}

.field-label {
	font-size: 0.875rem;
	font-weight: 500;
	color: #6b7280;
}

.tooltip-demos {
	display: flex;
	gap: 0.5rem;
	flex-wrap: wrap;
}

.popover-content {
	padding: 1rem;
	max-width: 300px;

	h6 {
		margin: 0 0 0.5rem 0;
		font-weight: 600;
	}

	p {
		margin: 0 0 1rem 0;
		color: #6b7280;
		font-size: 0.875rem;
	}
}

.flex {
	display: flex;
}

.gap-2 {
	gap: 0.5rem;
}

.mt-3 {
	margin-top: 0.75rem;
}

/* Sample button styling */
.sample-button {
	background: var(--p-button-primary-background, #3b82f6);
	color: var(--p-button-primary-color, white);
	border: 1px solid var(--p-button-primary-border-color, #3b82f6);
	padding: 0.5rem 1rem;
	border-radius: var(--p-button-border-radius, 6px);
	font-size: 0.875rem;
	cursor: pointer;
	pointer-events: none;
}

/* Popover Preview */
.popover-preview {
	position: relative;
	display: inline-block;
}

.popover-static {
	background: var(--p-popover-background, white);
	border: 1px solid var(--p-popover-border-color, #e5e7eb);
	border-radius: var(--p-popover-border-radius, 6px);
	box-shadow: var(--p-popover-shadow, 0 4px 6px -1px rgba(0, 0, 0, 0.1));
	min-width: 200px;
}

.popover-content {
	padding: 1rem;

	h6 {
		margin: 0 0 0.5rem 0;
		font-weight: 600;
		color: var(--p-popover-color, #374151);
	}

	p {
		margin: 0 0 1rem 0;
		color: var(--p-popover-color, #6b7280);
		font-size: 0.875rem;
	}
}

.popover-action-btn {
	background: var(--p-button-secondary-background, #f3f4f6);
	color: var(--p-button-secondary-color, #374151);
	border: 1px solid var(--p-button-secondary-border-color, #d1d5db);
	padding: 0.25rem 0.75rem;
	border-radius: var(--p-button-border-radius, 6px);
	font-size: 0.75rem;
	cursor: pointer;
	pointer-events: none;
}

/* Tooltip Preview */
.tooltip-preview {
	position: relative;
	display: inline-block;
}

.tooltip-static {
	background: var(--p-tooltip-background, #374151);
	color: var(--p-tooltip-color, white);
	padding: 0.5rem 0.75rem;
	border-radius: var(--p-tooltip-border-radius, 4px);
	font-size: 0.75rem;
	white-space: nowrap;
	box-shadow: var(--p-tooltip-shadow, 0 2px 4px rgba(0, 0, 0, 0.1));
	display: inline-block;
}

/* Confirm Dialog Preview */
.confirm-dialog-preview {
	position: relative;
	height: 200px;
	border-radius: 8px;
	overflow: hidden;
	border: 1px solid #e5e7eb;
}

.confirm-overlay {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	background: var(--p-overlay-mask-background, rgba(0, 0, 0, 0.4));
	display: flex;
	align-items: center;
	justify-content: center;
}

.confirm-container {
	background: var(--p-confirmdialog-background, white);
	border-radius: var(--p-confirmdialog-border-radius, 6px);
	box-shadow: var(--p-confirmdialog-shadow, 0 4px 16px rgba(0, 0, 0, 0.15));
	min-width: 18rem;
	max-width: 90%;
}

.confirm-header {
	display: flex;
	align-items: center;
	gap: 0.75rem;
	padding: 1rem 1.25rem;
	border-bottom: 1px solid var(--p-confirmdialog-header-border-color, #e5e7eb);
}

.confirm-icon {
	color: var(--p-confirmdialog-icon-color, #f59e0b);
	font-size: 1.25rem;
}

.confirm-title {
	font-weight: 600;
	color: var(--p-confirmdialog-title-color, #374151);
}

.confirm-content {
	padding: 1rem 1.25rem;
}

.confirm-content p {
	margin: 0;
	color: var(--p-confirmdialog-content-color, #374151);
}

.confirm-footer {
	display: flex;
	align-items: center;
	justify-content: flex-end;
	gap: 0.5rem;
	padding: 1rem 1.25rem;
	border-top: 1px solid var(--p-confirmdialog-header-border-color, #e5e7eb);
}

.confirm-btn {
	padding: 0.5rem 1rem;
	border-radius: var(--p-button-border-radius, 6px);
	font-size: 0.875rem;
	font-weight: 500;
	cursor: pointer;
	border: 1px solid transparent;
	pointer-events: none;
}

.confirm-btn-secondary {
	background: var(--p-button-secondary-background, transparent);
	color: var(--p-button-secondary-color, #6b7280);
	border-color: var(--p-button-secondary-border-color, #d1d5db);
}

.confirm-btn-primary {
	background: var(--p-button-primary-background, #3b82f6);
	color: var(--p-button-primary-color, white);
}

/* Sidebar Preview */
.sidebar-preview {
	position: relative;
	height: 200px;
	border-radius: 8px;
	overflow: hidden;
	border: 1px solid #e5e7eb;
	background: #f9fafb;
}

.sidebar-container {
	position: absolute;
	top: 0;
	right: 0;
	width: 70%;
	height: 100%;
	background: var(--p-sidebar-background, white);
	border-left: 1px solid var(--p-sidebar-border-color, #e5e7eb);
	box-shadow: var(--p-sidebar-shadow, -2px 0 4px rgba(0, 0, 0, 0.1));
}

.sidebar-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 1rem;
	border-bottom: 1px solid var(--p-sidebar-header-border-color, #e5e7eb);
}

.sidebar-title {
	font-weight: 600;
	color: var(--p-sidebar-title-color, #374151);
}

.sidebar-close-btn {
	background: transparent;
	border: none;
	color: var(--p-sidebar-close-button-color, #6b7280);
	cursor: pointer;
	padding: 0.25rem;
	pointer-events: none;
}

.sidebar-content {
	padding: 1rem;
}

.sidebar-content p {
	margin: 0 0 1rem 0;
	color: var(--p-sidebar-content-color, #374151);
	font-size: 0.875rem;
}

.sidebar-actions {
	display: flex;
	gap: 0.5rem;
}

.sidebar-btn {
	padding: 0.375rem 0.75rem;
	border-radius: var(--p-button-border-radius, 6px);
	font-size: 0.75rem;
	cursor: pointer;
	border: 1px solid transparent;
	pointer-events: none;
}

.sidebar-btn-primary {
	background: var(--p-button-primary-background, #3b82f6);
	color: var(--p-button-primary-color, white);
}

.sidebar-btn-secondary {
	background: var(--p-button-secondary-background, transparent);
	color: var(--p-button-secondary-color, #6b7280);
	border-color: var(--p-button-secondary-border-color, #d1d5db);
}

</style>